import { useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { AddDialog } from '@/components/AddDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  deleteSuperuserUser,
  querySuperuserUsers,
  revokeSuperuserUserSessions,
  setSuperuserUserBlocked,
  updateSuperuserUser,
} from '@/features/superuser/data/superuserUsers';
import { useNotifications } from '@/providers/notificationHooks';
import { queryKeys } from '@/shared/hooks';
import { useAccessToken } from '@/shared/hooks/auth';
import { Inline, Surface, Text } from '@/ui';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { formatDate } from '@/ui/formatters/formatDate';
import { Button, TextField, type GridColDef } from '@/ui/primitives';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { UserListItemDto } from '@flowqa/shared';

type EditUserForm = {
  id: string;
  originalEmail: string;
  email: string;
  firstName: string;
  lastName: string;
  blocked: boolean;
  deleted: boolean;
};

export const SuperuserUsersTable = (): ReactElement => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { notify } = useNotifications();
  const [editingForm, setEditingForm] = useState<EditUserForm | null>(null);
  const [inactiveReason, setInactiveReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [revokingAccess, setRevokingAccess] = useState(false);
  const [revokingSessions, setRevokingSessions] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeEmailInput, setRevokeEmailInput] = useState('');
  const [revokeSessionsOpen, setRevokeSessionsOpen] = useState(false);
  const [revokeSessionsEmailInput, setRevokeSessionsEmailInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pending = saving || updatingStatus || revokingAccess || revokingSessions;

  const closeDialog = useCallback(() => {
    if (pending) return;
    setEditingForm(null);
    setInactiveReason('');
    setRevokeOpen(false);
    setRevokeEmailInput('');
    setRevokeSessionsOpen(false);
    setRevokeSessionsEmailInput('');
    setErrorMessage(null);
  }, [pending]);

  const openDialog = useCallback((row: UserListItemDto) => {
    setEditingForm({
      id: row.id,
      originalEmail: row.email,
      email: row.email,
      firstName: row.firstName ?? '',
      lastName: row.lastName ?? '',
      blocked: Boolean(row.blockedAt),
      deleted: Boolean(row.deletedAt),
    });
    setInactiveReason(row.blockedReason ?? '');
    setRevokeOpen(false);
    setRevokeEmailInput('');
    setRevokeSessionsOpen(false);
    setRevokeSessionsEmailInput('');
    setErrorMessage(null);
  }, []);

  const saveDialog = useCallback(async (): Promise<void> => {
    if (!editingForm) return;
    if (editingForm.deleted) {
      setErrorMessage('Cannot edit a revoked user.');
      return;
    }
    const email = editingForm.email.trim().toLowerCase();
    if (email.length === 0) {
      setErrorMessage('Email is required.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await updateSuperuserUser(
        {
          id: editingForm.id,
          email,
          firstName: editingForm.firstName.trim().length > 0 ? editingForm.firstName.trim() : null,
          lastName: editingForm.lastName.trim().length > 0 ? editingForm.lastName.trim() : null,
        },
        accessToken,
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.superuserUsers() });
      notify({ message: 'User updated', tone: 'green' });
      setEditingForm((current) =>
        current
          ? {
              ...current,
              email,
              originalEmail: email,
              firstName: editingForm.firstName.trim(),
              lastName: editingForm.lastName.trim(),
            }
          : current,
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  }, [accessToken, editingForm, notify, queryClient]);

  const setUserBlockedState = useCallback(async (): Promise<void> => {
    if (!editingForm) return;
    if (editingForm.deleted) {
      setErrorMessage('Cannot change status for a revoked user.');
      return;
    }
    const nextBlocked = !editingForm.blocked;
    setUpdatingStatus(true);
    setErrorMessage(null);
    try {
      const updated = await setSuperuserUserBlocked(
        {
          id: editingForm.id,
          blocked: nextBlocked,
          reason: nextBlocked ? inactiveReason.trim() || null : null,
        },
        accessToken,
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.superuserUsers() });
      setEditingForm((current) =>
        current
          ? {
              ...current,
              blocked: Boolean(updated.blockedAt),
            }
          : current,
      );
      if (!nextBlocked) {
        setInactiveReason('');
      }
      notify({
        message: nextBlocked ? 'User set to inactive' : 'User set to active',
        tone: nextBlocked ? 'orange' : 'green',
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update user status');
    } finally {
      setUpdatingStatus(false);
    }
  }, [accessToken, editingForm, inactiveReason, notify, queryClient]);

  const revokeAccess = useCallback(async (): Promise<void> => {
    if (!editingForm) return;
    setRevokingAccess(true);
    setErrorMessage(null);
    try {
      await deleteSuperuserUser({ id: editingForm.id }, accessToken);
      await queryClient.invalidateQueries({ queryKey: queryKeys.superuserUsers() });
      notify({ message: `Revoked access for ${editingForm.originalEmail}`, tone: 'blue' });
      setRevokeOpen(false);
      setRevokeEmailInput('');
      setEditingForm(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to revoke user access';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    } finally {
      setRevokingAccess(false);
    }
  }, [accessToken, editingForm, notify, queryClient]);

  const revokeSessions = useCallback(async (): Promise<void> => {
    if (!editingForm) return;
    if (editingForm.deleted) {
      setErrorMessage('Cannot revoke sessions for a revoked user.');
      return;
    }
    setRevokingSessions(true);
    setErrorMessage(null);
    try {
      await revokeSuperuserUserSessions({ id: editingForm.id }, accessToken);
      setRevokeSessionsOpen(false);
      setRevokeSessionsEmailInput('');
      notify({ message: `Forced sign-out for ${editingForm.originalEmail}`, tone: 'blue' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to revoke user sessions';
      setErrorMessage(message);
      notify({ message, tone: 'red' });
    } finally {
      setRevokingSessions(false);
    }
  }, [accessToken, editingForm, notify]);

  const columns = useMemo<Array<GridColDef<UserListItemDto>>>(() => {
    const emailColumn: GridColDef<UserListItemDto> = {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      valueGetter: (_value, row) => row.email,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.email}
        </Text>
      ),
    };
    const nameColumn: GridColDef<UserListItemDto> = {
      field: 'name',
      headerName: 'Name',
      flex: 1.1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const firstName = params.row.firstName?.trim() ?? '';
        const lastName = params.row.lastName?.trim() ?? '';
        const fullName = [firstName, lastName].filter((segment) => segment.length > 0).join(' ');
        return (
          <Text role="body" component="span">
            {fullName.length > 0 ? fullName : '-'}
          </Text>
        );
      },
    };
    const createdAtColumn: GridColDef<UserListItemDto> = {
      field: 'createdAt',
      headerName: 'Signed Up',
      flex: 1,
      type: 'date',
      valueGetter: (_value, row) => {
        const raw = row.createdAt;
        if (raw instanceof Date) return raw;
        if (typeof raw === 'string') {
          const parsed = new Date(raw);
          return Number.isNaN(parsed.valueOf()) ? null : parsed;
        }
        return null;
      },
      renderCell: (params) => (
        <Text role="body" component="span">
          {formatDate(params.row.createdAt) || '-'}
        </Text>
      ),
    };
    const statusColumn: GridColDef<UserListItemDto> = {
      field: 'blockedAt',
      headerName: 'Status',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const label = row.deletedAt ? 'Revoked' : row.blockedAt ? 'Inactive' : 'Active';
        return (
          <Text role="body" component="span" transform="uppercase">
            {label}
          </Text>
        );
      },
    };
    const actionColumn: GridColDef<UserListItemDto> = {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="sm"
          fullWidthOnMobile={false}
          onClick={() => {
            openDialog(params.row);
          }}
        >
          Edit
        </Button>
      ),
    };

    return [emailColumn, nameColumn, createdAtColumn, statusColumn, actionColumn];
  }, [openDialog]);

  const queryFn = async (
    input: GridQueryInput,
  ): Promise<{ items: UserListItemDto[]; total: number; page: number; pageSize: number }> => {
    return await querySuperuserUsers(input, accessToken);
  };

  const expectedEmail = editingForm?.originalEmail.trim().toLowerCase() ?? '';
  const revokeEmailMatches =
    expectedEmail.length > 0 && revokeEmailInput.trim().toLowerCase() === expectedEmail;
  const revokeSessionsEmailMatches =
    expectedEmail.length > 0 && revokeSessionsEmailInput.trim().toLowerCase() === expectedEmail;
  const disableSave = !editingForm || editingForm.email.trim().length === 0 || pending;

  return (
    <>
      <Surface padding="none" border={false} sx={{ width: '100%', minWidth: 0 }}>
        <ServerDataTable<UserListItemDto>
          queryPath="superuser-users"
          queryKeyPrefix={queryKeys.superuserUsers()}
          queryFn={queryFn}
          columns={columns}
          getRowId={(row) => row.id}
          token={accessToken}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={5}
          autoHeight
          noRowsText="No users found"
          disableColumnMenu
        />
      </Surface>

      <AddDialog
        open={editingForm !== null}
        title="Edit User"
        saving={saving}
        disableSave={disableSave}
        errorMessage={errorMessage}
        onClose={closeDialog}
        onSave={saveDialog}
        saveLabel="Save Changes"
        maxWidth="sm"
      >
        <TextField
          label="Email"
          value={editingForm?.email ?? ''}
          onChange={(event) => {
            const value = event.target.value;
            setEditingForm((current) => (current ? { ...current, email: value } : current));
          }}
          disabled={pending || editingForm?.deleted === true}
          fullWidth
        />
        <Inline gap="sm">
          <TextField
            label="First Name"
            value={editingForm?.firstName ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setEditingForm((current) => (current ? { ...current, firstName: value } : current));
            }}
            disabled={pending || editingForm?.deleted === true}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={editingForm?.lastName ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setEditingForm((current) => (current ? { ...current, lastName: value } : current));
            }}
            disabled={pending || editingForm?.deleted === true}
            fullWidth
          />
        </Inline>
        <Text role="caption" color="textSecondary">
          Status: {editingForm?.deleted ? 'Revoked' : editingForm?.blocked ? 'Inactive' : 'Active'}
        </Text>
        <TextField
          label="Inactive Reason (Optional)"
          value={inactiveReason}
          onChange={(event) => {
            setInactiveReason(event.target.value);
          }}
          disabled={pending || editingForm?.deleted === true}
          fullWidth
        />
        <Inline gap="sm" justify="end">
          <Button
            variant="secondary"
            onClick={() => {
              void setUserBlockedState();
            }}
            disabled={pending || !editingForm || editingForm.deleted}
          >
            {editingForm?.blocked ? 'Set Active' : 'Set Inactive'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setRevokeSessionsOpen(true);
              setRevokeSessionsEmailInput('');
              setErrorMessage(null);
            }}
            disabled={pending || !editingForm || editingForm.deleted}
          >
            Force Sign Out
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setRevokeOpen(true);
              setRevokeEmailInput('');
              setErrorMessage(null);
            }}
            disabled={pending || !editingForm || editingForm.deleted}
          >
            Revoke Access
          </Button>
        </Inline>
      </AddDialog>

      <ConfirmDialog
        open={revokeOpen}
        title="Revoke User Access"
        description={
          editingForm
            ? `Type ${editingForm.originalEmail} to confirm revoking access for this user.`
            : 'Type the user email to confirm.'
        }
        confirmLabel="Revoke Access"
        confirmColor="red"
        loading={revokingAccess}
        confirmDisabled={!revokeEmailMatches || !editingForm}
        onClose={() => {
          if (pending) return;
          setRevokeOpen(false);
          setRevokeEmailInput('');
        }}
        onConfirm={() => {
          void revokeAccess();
        }}
        maxWidth="sm"
      >
        <TextField
          label="Confirm Email"
          value={revokeEmailInput}
          onChange={(event) => {
            setRevokeEmailInput(event.target.value);
          }}
          fullWidth
          autoFocus
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={revokeSessionsOpen}
        title="Force Sign Out Everywhere"
        description={
          editingForm
            ? `Type ${editingForm.originalEmail} to confirm signing this user out of all active sessions.`
            : 'Type the user email to confirm.'
        }
        confirmLabel="Force Sign Out"
        confirmColor="orange"
        loading={revokingSessions}
        confirmDisabled={!revokeSessionsEmailMatches || !editingForm}
        onClose={() => {
          if (pending) return;
          setRevokeSessionsOpen(false);
          setRevokeSessionsEmailInput('');
        }}
        onConfirm={() => {
          void revokeSessions();
        }}
        maxWidth="sm"
      >
        <TextField
          label="Confirm Email"
          value={revokeSessionsEmailInput}
          onChange={(event) => {
            setRevokeSessionsEmailInput(event.target.value);
          }}
          fullWidth
          autoFocus
        />
      </ConfirmDialog>
    </>
  );
};
